import { AgGridReact } from "ag-grid-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  GetRowIdParams,
  GridReadyEvent,
  IRowNode,
  PostSortRowsParams,
  RowHeightParams,
  RowSelectedEvent,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-balham.css"; // Optional Theme applied to the grid
import "ag-grid-community/styles/ag-theme-quartz.css"; // Optional Theme applied to the grid
import "ag-grid-enterprise";
import CreationDetailRenderer from "./CreationDetailRenderer";
import DraftLegAddButton from "./DraftLegAddButton";
import { prefetchSwapReferenceDataWhenIdle } from "@/components/widgets/tradeFromScratch/helper/tradeFromScratchHelper";
import { useTradeDateRollover } from "@/components/widgets/tradeFromScratch/helper/useTradeDateRollover";
import { isDraftLegRow } from "./helper/cellRenderers/DraftLeg/useDraftLegTicket";
import {
  insertDraftLegActionColDefs,
  wrapColDefsForDraftLegs,
} from "./helper/colDefs/draftLegColDefs";
import { returnDraftLegContextMenuItems } from "./helper/draftLegContextMenu";
import { returnBlockEditContextMenuItems } from "./helper/blockEditActions";
import {
  returnCreationGridRows,
  sortNewDraftLegsFirst,
} from "./helper/draftLegRowHelpers";
import draftLegsStore, { IDraftLeg } from "@/store/draftLegsStore";
import featureFlagStore from "@/store/featureFlagStore";

// custom helper functions:
import { Button } from "@/components/UI/Button";
import {
  applyColumnWidthMultiplier,
  applyDefaultMinWidths,
  debounce,
  getColumnWidthMultiplier,
  getFontSizePx,
  getHeaderHeight,
  getMainBlueActionButtonSize,
  getRowHeight,
} from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";
import {
  applyColumnStateToAGGridTable,
  applyFiltersToAGGridTable,
  manageGridEventListeners,
  readGridRefAndUpdateStoreAndDatabaseWithNewValues,
} from "@/lib/AGGridHelpers";
import AGGridNoRowsOverlay from "@/components/UI/AGGridNoRowsOverlay";
import { syncNoRowsOverlay } from "@/lib/agGridOverlayHelper";
import { returnOpenWidgetsObject } from "@/lib/widgetHelperFunctions";
import importBlocksAndAllocationsStore, { IImportJsonBlocks } from "@/store/importBlocksAndAllocationsStore";
import useTableUIStore from "@/store/tableUIStore";
import agGridFlashNotifications from "@/store/notifications/agGridFlashNotifications";
import tradeCreationInProcessStore from "@/store/tradeCreationInProcessStore";
import { Tooltip } from "react-tooltip";
import { cubeCustomColors } from "../../../../tailwind.config";
import { returnCreationTableColDefs } from "./helper/colDefs/creationTableColDefs";
import { defaultColDefTradeCreation } from "./helper/colDefs/defaultColDefTradeCreation";
import { defaultSidebarsAGGridProps } from "./helper/creationSideBar";
import {
  adjustSelectedStateOfNodesIfNeeded,
  blockGridStylesTradeCreation,
  getContextMenuItemsTradeCreation,
  handelCreationTableSelection,
  splitBlocksFacade,
  returnFullAndPartialSelectedNodes,
  returnTrueIfNodesConatinNotDealt,
  returnTrueIfNodesContainFix,
  sendSelectedBlocksToSaveTrade,
} from "./helper/tradeCreationHelper";
const NO_DRAFT_LEGS: IDraftLeg[] = [];
// A draft row holds inputs, so it needs a little more height than a block row.
const DRAFT_LEG_ROW_HEIGHT = 34;

interface ICreationTable {
  cryptoID?: string | undefined;
  theme: string | undefined;
  getAccessTokenSilently: Function;
  importData: IImportJsonBlocks[];
}

const CreationTable = ({
  cryptoID,
  theme,
  getAccessTokenSilently,
  importData,
}: ICreationTable) => {
  const tableStripeOddTrue = useTableUIStore(
    (state) => state.tableStripeImportOn,
  ); // used to change the table stripe with settings.
  const fontSize = useTableUIStore((state) => state.fontSize);
  const rowHeight = useTableUIStore((state) => state.rowHeight);
  const creationColorLight = useTableUIStore(
    (state) => state.creationColorLight,
  );
  const creationColorDark = useTableUIStore(
    (state) => state.creationColorDark,
  );
  const creationDirectionEnabled = useTableUIStore(
    (state) => state.creationDirectionEnabled,
  );
  const creationBidPayColorLight = useTableUIStore(
    (state) => state.creationBidPayColorLight,
  );
  const creationBidPayColorDark = useTableUIStore(
    (state) => state.creationBidPayColorDark,
  );
  const creationAskReceiveColorLight = useTableUIStore(
    (state) => state.creationAskReceiveColorLight,
  );
  const creationAskReceiveColorDark = useTableUIStore(
    (state) => state.creationAskReceiveColorDark,
  );
  const creationFlashColorLight = useTableUIStore(
    (state) => state.creationFlashColorLight,
  );
  const creationFlashColorDark = useTableUIStore(
    (state) => state.creationFlashColorDark,
  );
  const testFlashCreation = useTableUIStore(
    (state) => state.testFlashCreation,
  );
  const storeRowFlashDuration = agGridFlashNotifications(
    (state) => state.creationFlashDuration,
  );

  const [rowData, setRowData] = useState(importData);
  useEffect(() => {
    setRowData(importData);
  }, [importData]);

  // LQC-2941 - unsaved legs, appended after the blocks. Behind tradeCreationLegs.
  const isTradeCreationLegsOn = featureFlagStore(
    (state) => state.tradeCreationLegs,
  );
  const draftLegs = draftLegsStore((state) =>
    cryptoID ? (state.draftLegsByWidget[cryptoID] ?? NO_DRAFT_LEGS) : NO_DRAFT_LEGS,
  );
  const gridRowData = useMemo(() => {
    if (!isTradeCreationLegsOn || draftLegs.length === 0) return rowData;
    // LQC-3027 - a block being edited is replaced, in place, by its edit draft.
    return returnCreationGridRows(rowData, draftLegs, cryptoID);
  }, [rowData, draftLegs, isTradeCreationLegsOn, cryptoID]);

  // Draft legs are tickets too, so they roll at midnight like Leg Creation (LQC-2981).
  useTradeDateRollover(isTradeCreationLegsOn);

  // Idle-time warm-up, so a "+ Leg" swap opens on loaded lists. Never before the blotter.
  useEffect(() => {
    if (!isTradeCreationLegsOn) return;
    return prefetchSwapReferenceDataWhenIdle(getAccessTokenSilently);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTradeCreationLegsOn]);

  useEffect(() => {
    const blockIdsToFlash = importBlocksAndAllocationsStore.getState().blockIdsToFlash;
    if (blockIdsToFlash.length === 0) return;

    importBlocksAndAllocationsStore.getState().updateBlockIdsToFlash([]);

    setTimeout(() => {
      if (!gridRef.current?.api) return;

      const rowNodes = blockIdsToFlash
        .map((id) => gridRef.current?.api.getRowNode(String(id)))
        .filter((n): n is NonNullable<typeof n> => n != null);

      if (rowNodes.length > 0) {
        gridRef.current.api.flashCells({
          rowNodes,
          flashDuration: storeRowFlashDuration,
          fadeDuration: 1000,
        });
      }
    }, 100);
  }, [rowData]);
  const gridRef = useRef<AgGridReact>(null);

  // Test flash from settings — flash the top row when triggered
  useEffect(() => {
    if (testFlashCreation > 0 && gridRef.current?.api) {
      const firstRow = gridRef.current.api.getDisplayedRowAtIndex(0);
      if (firstRow) {
        gridRef.current.api.flashCells({
          rowNodes: [firstRow],
          flashDuration: storeRowFlashDuration,
          fadeDuration: 1000,
        });
      }
    }
  }, [testFlashCreation]);

  const [canRecreateBlocks, setCanRecreateBlocks] = useState(false);
  const [
    areAnyBlocksSelectedStatusNotDealt,
    setAreAnyBlocksSelectedStatusNotDealt,
  ] = useState(false);
  // A FIX leg can't be split, so Split Leg is hidden while one is part-ticked.
  const [isAnyPartlySelectedBlockFix, setIsAnyPartlySelectedBlockFix] =
    useState(false);
  // Drives the orange filter indicator on the sidebar "Filters" tab button.
  // True whenever any column filter is active on the grid.
  const [isAnyFilterActive, setIsAnyFilterActive] = useState(false);

  // Mirror the header's orange active-filter state onto the sidebar tab.
  function handleCreationFilterChange(event: any) {
    setIsAnyFilterActive(event.api.isAnyFilterPresent());
  }

  // 13914 - Disable 'Save' and 'Recreate' buttons once clicked.
  const tradeCreationBusy = tradeCreationInProcessStore(
    (state) => state.isTradeCreationBusy,
  );
  const setTradeCreationBusy = tradeCreationInProcessStore(
    (state) => state.setIsTradeCreationBusy,
  );

  const dynamicRowHeight = useMemo(
    () => getRowHeight(rowHeight),
    [rowHeight],
  );

  const dynamicHeaderHeight = useMemo(
    () => getHeaderHeight(rowHeight),
    [rowHeight],
  );

  const columnWidthMultiplier = useMemo(
    () => getColumnWidthMultiplier(fontSize),
    [fontSize],
  );

  const coldefs = useMemo(() => {
    // applyDefaultMinWidths adds a real minWidth floor; applyColumnWidthMultiplier
    // then applies the font multiplier (incl. per-column differential scaling) and
    // strips its custom flags. This is the ONLY place the multiplier is applied —
    // the effect below merely re-asserts the resulting width on the grid.
    // LQC-2259 - deliberately not keyed on theme: a new colDefs array makes AG Grid
    // re-apply every colDef, overwriting the user's saved widths/visibility. Cells
    // that need theme read it themselves (CreationEffectiveDateCellRenderer).
    const blockCols = wrapColDefsForDraftLegs(
      returnCreationTableColDefs(getAccessTokenSilently),
    );
    // Action columns only exist while drafts can, so prod grids gain no empty columns.
    const cols = applyDefaultMinWidths(
      isTradeCreationLegsOn ? insertDraftLegActionColDefs(blockCols, cryptoID) : blockCols,
    );
    return applyColumnWidthMultiplier(cols, columnWidthMultiplier);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnWidthMultiplier, isTradeCreationLegsOn, cryptoID]);

  const getRowId = useCallback((params: GetRowIdParams) => {
    return params.data.id;
  }, []);

  const detailCellRenderer = useMemo(() => {
    return CreationDetailRenderer;
  }, []);

  const isRowSelectable = useCallback(
    (node: IRowNode) => !isDraftLegRow(node.data),
    [],
  );

  const postSortRows = useCallback(
    (params: PostSortRowsParams) => sortNewDraftLegsFirst(params.nodes),
    [],
  );

  // Detail rows size themselves (detailRowAutoHeight); only draft rows are overridden.
  const getRowHeightForRow = useCallback(
    (params: RowHeightParams) => {
      if (params.node.detail || !isDraftLegRow(params.data)) return undefined;
      return Math.max(dynamicRowHeight, DRAFT_LEG_ROW_HEIGHT);
    },
    [dynamicRowHeight],
  );

  // A new draft opens straight away so its portfolio can be picked.
  const expandDraftLeg = (draftId: string) => {
    setTimeout(() => {
      const api = gridRef.current?.api;
      const node = api?.getRowNode(draftId);
      if (!api || !node) return;
      node.setExpanded(true);
      // It lands on top, which may be scrolled out of view.
      api.ensureNodeVisible(node, "top");
    }, 50);
  };

  const sideBar = useMemo(() => {
    return defaultSidebarsAGGridProps;
  }, []);

  const style = useMemo(
    () => {
      const baseStyles = blockGridStylesTradeCreation(
        theme,
        tableStripeOddTrue,
      ) as React.CSSProperties;
      return {
        ...baseStyles,
        fontSize: `${getFontSizePx(fontSize)}px`,
      };
    },
    [
      theme,
      tableStripeOddTrue,
      fontSize,
      creationColorLight,
      creationColorDark,
      creationDirectionEnabled,
      creationBidPayColorLight,
      creationBidPayColorDark,
      creationAskReceiveColorLight,
      creationAskReceiveColorDark,
      creationFlashColorLight,
      creationFlashColorDark,
    ],
  );

  const dynamicButtonSize = useMemo(
    () => getMainBlueActionButtonSize(fontSize),
    [fontSize],
  );

  const popupParent = useMemo<HTMLElement | null>(() => {
    return document.querySelector(".GridContainer");
  }, []);

  // -- START of persisting table state to the widget -- //
  const debouncedSaveColumnState = debounce(() => {
    readGridRefAndUpdateStoreAndDatabaseWithNewValues(
      gridRef,
      getAccessTokenSilently,
      cryptoID,
    );
  }, 1000);

  // Add a ref to track if initial settings have been applied
  const initialSettingsAppliedRef = useRef(false);

  const onGridReady = useCallback(
    async (_params: GridReadyEvent) => {
      // Apply settings after a short delay to ensure grid is fully initialized
      // We use setTimeout because firstDataRendered doesn't fire reliably on tab switches
      setTimeout(() => {
        // Fetch the latest openWidgetObject from the store
        const latestOpenWidgetObject = cryptoID
          ? returnOpenWidgetsObject(cryptoID)
          : undefined;

        // Only apply settings on the very first render
        if (
          !initialSettingsAppliedRef.current &&
          gridRef.current &&
          latestOpenWidgetObject &&
          (latestOpenWidgetObject.filterModel ||
            latestOpenWidgetObject.columnStateForWidget)
        ) {
          applyColumnStateToAGGridTable(gridRef, latestOpenWidgetObject);
          applyFiltersToAGGridTable(gridRef, latestOpenWidgetObject);
          initialSettingsAppliedRef.current = true;
        }
      }, 100);

      // apply the Event listeners form AG Grid Helpers
      const cleanupEventListeners = manageGridEventListeners(
        gridRef,
        debouncedSaveColumnState,
      );
      return cleanupEventListeners; // Cleanup on unmount or dependency change
    },
    [cryptoID, debouncedSaveColumnState],
  );
  // -- END of persisting table state to the widget -- //

  // MAIN LOGIC -- Handling selected tables, ACTIONS and pop up messages:
  const onRowSelected = (params: RowSelectedEvent) => {
    // Loop blocks - If a block of 'Not Dealt' is selected, update hooks to disable buttons.
    const selectedBlocksContainNotDealt =
      returnTrueIfNodesConatinNotDealt(gridRef);
    if (selectedBlocksContainNotDealt) {
      setAreAnyBlocksSelectedStatusNotDealt(true);
    } else {
      setAreAnyBlocksSelectedStatusNotDealt(false);
    }
    //before matching table data, reset the pop up message if it's active.
    handelCreationTableSelection(params, gridRef, setCanRecreateBlocks);
    // After handelCreationTableSelection, which can tick or untick a leg's allocations.
    setIsAnyPartlySelectedBlockFix(
      returnTrueIfNodesContainFix(
        returnFullAndPartialSelectedNodes(gridRef).partiallySelectedNodes,
      ),
    );
  };

  useEffect(() => {
    // Loop blocks - If a block of 'Not Dealt' is selected, update hooks to disable buttons.
    const selectedBlocksContainNotDealt =
      returnTrueIfNodesConatinNotDealt(gridRef);
    if (selectedBlocksContainNotDealt) {
      setAreAnyBlocksSelectedStatusNotDealt(true);
    } else {
      setAreAnyBlocksSelectedStatusNotDealt(false);
    }

    const { partiallySelectedNodes } =
      returnFullAndPartialSelectedNodes(gridRef);
    if (!partiallySelectedNodes || partiallySelectedNodes.length == 0) {
      setCanRecreateBlocks(false);
    }
    setIsAnyPartlySelectedBlockFix(
      returnTrueIfNodesContainFix(partiallySelectedNodes),
    );

    // Run a function to loop for blocks that are in a partially selected state, but their allocations are all un selected.
    setTimeout(() => {
      adjustSelectedStateOfNodesIfNeeded(gridRef, partiallySelectedNodes);
    }, 50); // Set a timeout on the block check to allow time for detail row to re-create.
  }, [rowData, canRecreateBlocks]);

  // Re-assert the font-scaled widths when the font size changes. The widths are
  // already computed once in the coldefs memo above; AG Grid preserves a column's
  // existing width across columnDefs updates, so we push the new width imperatively
  // here. Do NOT multiply again — colDef.width is already scaled.
  useEffect(() => {
    if (gridRef.current?.api) {
      const allColumns = gridRef.current.api.getColumns();
      if (allColumns) {
        allColumns.forEach((column) => {
          const colDef = column.getColDef() as any;
          if (colDef.fixedWidth) return; // Skip fixed-width control columns
          if (colDef.width) {
            gridRef.current?.api.setColumnWidth(column, colDef.width);
          }
        });
      }
    }
  }, [fontSize, columnWidthMultiplier]);

  // 13711 - Recreating Blocks action.
  const splitBlocksRequest = async () => {
    setTradeCreationBusy(true); // Disable Trade Creation Buttons.
    splitBlocksFacade({ getAccessTokenSilently, gridRef });
  };

  // Action when sending blocks to the action summary.
  // SENDING BLOCKS TO ACTION SUMMARY
  const selectOrSaveAtTradeCreation = (shouldExpand: boolean) => {
    setTradeCreationBusy(true); // Disable Trade Creation Buttons.
    const { fullySelectedNodes } = returnFullAndPartialSelectedNodes(gridRef);

    sendSelectedBlocksToSaveTrade(
      fullySelectedNodes,
      shouldExpand,
      getAccessTokenSilently,
    );
  };

  // Helper function to adjust color brightness (for stripe variant)
  const adjustBrightness = (hex: string, percent: number): string => {
    const num = parseInt(hex.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return (
      "#" +
      (
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 1 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    );
  };

  // Get row style based on direction (Sell/Pay or Buy/Receive)
  const getRowStyle = useCallback(
    (params: any) => {
      if (!creationDirectionEnabled) {
        return undefined; // Use default styling from CSS
      }

      const data = params.data;
      if (!data) return undefined;

      // Get direction from data
      const direction = data.direction;
      if (!direction) {
        return undefined;
      }

      // Determine base color based on direction and theme
      let baseColor: string;
      const directionLower = direction.toLowerCase();

      if (directionLower === "sell" || directionLower === "pay") {
        baseColor =
          theme === "light"
            ? creationBidPayColorLight
            : creationBidPayColorDark;
      } else if (directionLower === "buy" || directionLower === "receive") {
        baseColor =
          theme === "light"
            ? creationAskReceiveColorLight
            : creationAskReceiveColorDark;
      } else {
        return undefined;
      }

      // Apply stripe if needed
      if (tableStripeOddTrue && params.node.rowIndex % 2 !== 0) {
        const adjustAmount = theme === "light" ? -5 : 5;
        baseColor = adjustBrightness(baseColor, adjustAmount);
      }

      // Create hover color as a darker/lighter shade
      const hoverAdjustAmount = theme === "light" ? -10 : 10;
      const hoverColor = adjustBrightness(baseColor, hoverAdjustAmount);

      return {
        background: baseColor,
        "--ag-row-hover-color": hoverColor,
      };
    },
    [
      creationDirectionEnabled,
      theme,
      tableStripeOddTrue,
      creationBidPayColorLight,
      creationBidPayColorDark,
      creationAskReceiveColorLight,
      creationAskReceiveColorDark,
    ],
  );

  //tradeCreationBusy used when actioning the endpoint - Should be returned active once button has completed API call or failed validation to access endpoint.
  return (
    <div className="flex h-full w-full flex-col">
      <div className="mt-1 flex space-x-3 py-2 ">
        <Button
          disabled={
            canRecreateBlocks ||
            tradeCreationBusy ||
            areAnyBlocksSelectedStatusNotDealt
          }
          className={`${canRecreateBlocks || tradeCreationBusy || areAnyBlocksSelectedStatusNotDealt ? "cursor-not-allowed opacity-50" : ""}`}
          variant="mainBlueAction"
          data-tooltip-id={
            canRecreateBlocks || areAnyBlocksSelectedStatusNotDealt
              ? "disabledButtons"
              : ""
          }
          size={dynamicButtonSize}
          data-testid="selectPanelBtn"
          onClick={() => selectOrSaveAtTradeCreation(true)}
        >
          Select Panel
        </Button>
        <Button
          disabled={
            canRecreateBlocks ||
            tradeCreationBusy ||
            areAnyBlocksSelectedStatusNotDealt
          }
          className={`${canRecreateBlocks || tradeCreationBusy || areAnyBlocksSelectedStatusNotDealt ? "cursor-not-allowed opacity-50" : ""}`}
          variant="secondaryBlueActionAtTradeCreation"
          size={dynamicButtonSize}
          data-tooltip-id={
            canRecreateBlocks || areAnyBlocksSelectedStatusNotDealt
              ? "disabledButtons"
              : ""
          }
          data-testid="saveTradeBtn"
          onClick={() => selectOrSaveAtTradeCreation(false)}
        >
          Save Trade
        </Button>
        <Button
          disabled={tradeCreationBusy}
          variant="secondaryBlueActionAtTradeCreation"
          className={`${canRecreateBlocks && !areAnyBlocksSelectedStatusNotDealt && !isAnyPartlySelectedBlockFix ? "block" : "hidden"} ${tradeCreationBusy ? "cursor-not-allowed opacity-50" : ""}`}
          size={dynamicButtonSize}
          data-testid="RecreateBlocksBtn"
          onClick={() => splitBlocksRequest()}
        >
          Split Leg
        </Button>
        {/* Right-aligned like the Leg Creation instrument picker. ! beats space-x's margin. */}
        {isTradeCreationLegsOn && cryptoID && (
          <div className="!ml-auto pr-1">
            <DraftLegAddButton
              widgetId={cryptoID}
              getAccessTokenSilently={getAccessTokenSilently}
              onAdded={expandDraftLeg}
            />
          </div>
        )}
      </div>
      <div className="flex w-full flex-grow">
        <div
          className={`${theme == "dark" ? "ag-theme-balham-dark" : "ag-theme-balham"} ${isAnyFilterActive ? "filterActiveSideBar" : ""} topAlignedDetailRows h-full w-full border-l-2 border-[#E7EAEB] dark:border-[#252525]`}
          style={style}
        >
          <AgGridReact
            ref={gridRef}
            rowData={gridRowData}
            isRowSelectable={isRowSelectable}
            postSortRows={postSortRows}
            getRowHeight={getRowHeightForRow}
            columnDefs={coldefs}
            columnMenu={"new"} //do not delete, affects header menus - zallows the filter icon to be visible
            defaultColDef={defaultColDefTradeCreation}
            suppressAggFuncInHeader={true}
            // Header tooltips (the Edit pencil) open at once, as on the blotter.
            tooltipShowDelay={0}
            valueCache={true}
            getRowId={getRowId}
            masterDetail={true}
            detailCellRenderer={detailCellRenderer}
            keepDetailRows={true}
            onRowSelected={onRowSelected}
            rowSelection={"multiple"}
            suppressRowClickSelection={true}
            detailRowAutoHeight={true}
            rowGroupPanelShow={"always"}
            groupDisplayType={"groupRows"} // removed the left hand side checkbox.
            sideBar={sideBar}
            onFilterChanged={handleCreationFilterChange}
            // LQC-2482: AG Grid 31 shows nothing when a filter leaves 0 rows.
            // Re-evaluate on upgrade to >= 35 (built-in "No matching rows" overlay).
            onModelUpdated={(params) => syncNoRowsOverlay(params.api, false)}
            noRowsOverlayComponent={AGGridNoRowsOverlay}
            onGridReady={onGridReady}
            scrollbarWidth={6}
            getContextMenuItems={(params) =>
              params.node && isDraftLegRow(params.node.data)
                ? returnDraftLegContextMenuItems(params.node, params.node.data)
                : [
                    // Edits render as draft rows, so only with tradeCreationLegs on, as the pencil.
                    ...(isTradeCreationLegsOn &&
                    !tradeCreationInProcessStore.getState().isTradeCreationBusy
                      ? returnBlockEditContextMenuItems(
                          params.node?.data,
                          cryptoID,
                          getAccessTokenSilently,
                          params.api,
                        )
                      : []),
                    ...getContextMenuItemsTradeCreation(params, getAccessTokenSilently),
                  ]
            }
            getRowStyle={getRowStyle}
            rowHeight={dynamicRowHeight}
            headerHeight={dynamicHeaderHeight}
            singleClickEdit={true}
            stopEditingWhenCellsLoseFocus={true}
            popupParent={popupParent}
          />
        </div>
        <Tooltip
          id={"disabledButtons"}
          place="bottom-start"
          variant="info"
          className="z-20"
          opacity={1}
          style={{
            color: theme == "dark" ? "white" : "black",
            backgroundColor:
              theme == "dark" ? "#2d2d2d" : cubeCustomColors.cubeBase[700],
            margin: "0px",
            padding: "3px",
            paddingLeft: "5px",
            paddingRight: "5px",
          }}
          offset={20}
          border="1px solid gray"
        >
          {areAnyBlocksSelectedStatusNotDealt
            ? "Cannot 'Save Trade' or 'Split Leg' with legs at status 'Not Dealt', remove these legs or 'Re-Open' them"
            : isAnyPartlySelectedBlockFix
              ? "A FIX leg cannot be split, fully select the leg"
              : "Either split the partially selected leg or fully select the leg"}
        </Tooltip>
      </div>
    </div>
  );
};

export default CreationTable;
